import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle,
  Clock,
  CreditCard,
  DollarSign,
  Download,
  FileText,
  Percent,
  RefreshCw,
  Save,
  Search,
  Settings,
  ShieldCheck,
  Table2,
  TrendingUp,
  WalletCards,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import AdminPageShell from "../components/AdminPageShell";
import API from "../../../util/API";

const cx = (...arr) => arr.filter(Boolean).join(" ");

const KNOWN_PROVIDERS = [
  { value: "all", label: "All gateways" },
  { value: "stripe", label: "Stripe" },
  { value: "paypal", label: "PayPal" },
  { value: "wise", label: "Wise" },
  { value: "cashapp", label: "Cash App" },
  { value: "sslcommerz", label: "SSLCommerz" },
  { value: "paystation", label: "PayStation" },
  { value: "nogod", label: "Nagad" },
  { value: "manual", label: "Manual" },
];

const todayInput = () => new Date().toISOString().slice(0, 10);

const daysAgoInput = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - (days - 1));
  return date.toISOString().slice(0, 10);
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const money = (value) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const number = (value) => new Intl.NumberFormat().format(Number(value || 0));
const percent = (value) => `${Number(value || 0).toFixed(1)}%`;

const normalizeChargeDraftRow = (row = {}) => ({
  provider: String(row.provider || "").trim().toLowerCase(),
  label: row.label || row.provider || "Gateway",
  percent: String(Number.isFinite(Number(row.percent)) ? Number(row.percent) : 0),
  fixedUsd: String(Number.isFinite(Number(row.fixedUsd)) ? Number(row.fixedUsd) : 0),
  enabled: row.enabled !== false,
  note: row.note || "",
});

const Card = ({ children, className = "" }) => (
  <div className={cx("rounded-2xl border border-slate-200 bg-white", className)}>{children}</div>
);

const Button = ({ variant = "secondary", className = "", ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
      variant === "primary"
        ? "bg-slate-900 text-white hover:bg-[#D2042D]"
        : "border border-slate-200 bg-white text-slate-900 hover:bg-slate-50",
      className
    )}
  />
);

const StatusPill = ({ value }) => {
  const status = String(value || "").toLowerCase();
  const cls =
    status === "paid" || status === "succeeded" || status === "delivered"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : status === "failed" || status === "cancelled" || status === "expired"
        ? "border-rose-200 bg-rose-50 text-rose-700"
        : status === "pending" || status === "requires_action" || status === "processing"
          ? "border-amber-200 bg-amber-50 text-amber-700"
          : "border-slate-200 bg-slate-50 text-slate-700";

  return (
    <span className={cx("inline-flex items-center rounded-full border px-2 py-1 text-xs font-bold", cls)}>
      {value || "-"}
    </span>
  );
};

const csvValue = (value) => {
  if (value === null || value === undefined) return "";
  const text = Array.isArray(value) ? value.join("; ") : String(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const downloadCsv = (fileName, rows, columns) => {
  const header = columns.map((column) => csvValue(column.label)).join(",");
  const body = rows.map((row) => columns.map((column) => csvValue(column.value(row))).join(","));
  const csv = [header, ...body].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const presetRanges = [
  { id: "7d", label: "7 days", from: () => daysAgoInput(7), to: todayInput },
  { id: "30d", label: "30 days", from: () => daysAgoInput(30), to: todayInput },
  { id: "90d", label: "90 days", from: () => daysAgoInput(90), to: todayInput },
  {
    id: "month",
    label: "This month",
    from: () => {
      const date = new Date();
      date.setDate(1);
      return date.toISOString().slice(0, 10);
    },
    to: todayInput,
  },
  {
    id: "year",
    label: "This year",
    from: () => `${new Date().getFullYear()}-01-01`,
    to: todayInput,
  },
];

export default function FinanceManagement() {
  const [from, setFrom] = useState(daysAgoInput(30));
  const [to, setTo] = useState(todayInput());
  const [provider, setProvider] = useState("all");
  const [activeTab, setActiveTab] = useState("overview");
  const [search, setSearch] = useState("");
  const [paymentSearch, setPaymentSearch] = useState("");
  const [payload, setPayload] = useState(null);
  const [chargeDraftRows, setChargeDraftRows] = useState([]);
  const [chargeSaving, setChargeSaving] = useState(false);
  const [chargeNotice, setChargeNotice] = useState("");
  const [chargeError, setChargeError] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let alive = true;
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await API.get("/finance/admin/overview", {
          params: { from, to, provider },
        });
        if (alive) setPayload(res.data || null);
      } catch (err) {
        if (alive) {
          setError(err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || "Failed to load finance data.");
          setPayload(null);
        }
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [from, to, provider, refreshKey]);

  const totals = payload?.totals || {};
  const dailyRows = useMemo(() => Array.isArray(payload?.daily) ? payload.daily : [], [payload]);
  const monthlyRows = useMemo(() => Array.isArray(payload?.monthly) ? payload.monthly : [], [payload]);
  const gatewayRows = useMemo(() => Array.isArray(payload?.gateways) ? payload.gateways : [], [payload]);
  const salesRows = useMemo(() => Array.isArray(payload?.salesRows) ? payload.salesRows : [], [payload]);
  const paymentRows = useMemo(() => Array.isArray(payload?.paymentRows) ? payload.paymentRows : [], [payload]);
  const refundRows = useMemo(() => Array.isArray(payload?.refundRows) ? payload.refundRows : [], [payload]);

  useEffect(() => {
    const rows = payload?.gatewayCharges?.rows;
    if (!Array.isArray(rows)) return;
    setChargeDraftRows(rows.map(normalizeChargeDraftRow).filter((row) => row.provider));
  }, [payload]);

  const providerOptions = useMemo(() => {
    const seen = new Set();
    const merged = [...KNOWN_PROVIDERS];
    gatewayRows.forEach((row) => merged.push({ value: row.provider, label: row.label || row.provider }));
    return merged.filter((item) => {
      const key = item.value;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [gatewayRows]);

  const exceptionRows = useMemo(() => {
    const exceptions = payload?.exceptions || {};
    return Object.entries(exceptions).flatMap(([group, rows]) =>
      (Array.isArray(rows) ? rows : []).map((row) => ({
        ...row,
        group,
      }))
    );
  }, [payload]);

  const filteredSalesRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return salesRows;
    return salesRows.filter((row) =>
      [
        row.orderId,
        row.customer,
        row.email,
        row.providerLabel,
        row.paymentStatus,
        row.fulfillmentStatus,
        row.country,
      ].some((value) => String(value || "").toLowerCase().includes(q))
    );
  }, [salesRows, search]);

  const filteredPaymentRows = useMemo(() => {
    const q = paymentSearch.trim().toLowerCase();
    if (!q) return paymentRows;
    return paymentRows.filter((row) =>
      [
        row.sessionId,
        row.orderId,
        row.providerLabel,
        row.status,
        row.billingCountry,
        row.failureReason,
      ].some((value) => String(value || "").toLowerCase().includes(q))
    );
  }, [paymentRows, paymentSearch]);

  const setPreset = (preset) => {
    setFrom(preset.from());
    setTo(preset.to());
  };

  const updateChargeDraft = (providerKey, patch) => {
    setChargeNotice("");
    setChargeError("");
    setChargeDraftRows((rows) =>
      rows.map((row) => (row.provider === providerKey ? { ...row, ...patch } : row))
    );
  };

  const saveGatewayCharges = async () => {
    setChargeSaving(true);
    setChargeNotice("");
    setChargeError("");
    try {
      const rows = chargeDraftRows.map((row) => {
        const percentValue = Number(row.percent);
        const fixedValue = Number(row.fixedUsd);
        if (!Number.isFinite(percentValue) || percentValue < 0 || percentValue > 100) {
          throw new Error(`Invalid percentage for ${row.label}`);
        }
        if (!Number.isFinite(fixedValue) || fixedValue < 0) {
          throw new Error(`Invalid fixed USD charge for ${row.label}`);
        }
        return {
          provider: row.provider,
          percent: percentValue,
          fixedUsd: fixedValue,
          enabled: row.enabled !== false,
          note: row.note || "",
        };
      });

      const res = await API.put("/finance/admin/gateway-charges", { rows });
      const savedRows = Array.isArray(res?.data?.rows) ? res.data.rows : rows;
      setChargeDraftRows(savedRows.map(normalizeChargeDraftRow).filter((row) => row.provider));
      setChargeNotice("Gateway charge settings saved.");
      setRefreshKey((value) => value + 1);
    } catch (err) {
      setChargeError(err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || "Failed to save gateway charges.");
    } finally {
      setChargeSaving(false);
    }
  };

  const exportName = (name) => `finance-${name}-${from}-to-${to}.csv`;

  const exportSales = () => downloadCsv(exportName("sales"), filteredSalesRows, [
    { label: "Order ID", value: (row) => row.orderId },
    { label: "Finance Date", value: (row) => row.financeDay },
    { label: "Created At", value: (row) => row.createdAt },
    { label: "Paid At", value: (row) => row.paidAt },
    { label: "Customer", value: (row) => row.customer },
    { label: "Email", value: (row) => row.email },
    { label: "Gateway", value: (row) => row.providerLabel },
    { label: "Payment Status", value: (row) => row.paymentStatus },
    { label: "Fulfillment Status", value: (row) => row.fulfillmentStatus },
    { label: "Currency", value: (row) => row.currency },
    { label: "Amount", value: (row) => row.amount },
    { label: "USD Amount", value: (row) => row.usdAmount },
    { label: "USD Source", value: (row) => row.usdSource },
    { label: "Gateway Charge %", value: (row) => row.gatewayChargePercent },
    { label: "Gateway Fixed USD", value: (row) => row.gatewayChargeFixedUsd },
    { label: "Gateway Charge USD", value: (row) => row.gatewayChargeUsd },
    { label: "Profit After Gateway Charge USD", value: (row) => row.profitAfterGatewayChargesUsd },
    { label: "Profit After Gateway Charge Margin", value: (row) => row.profitAfterGatewayChargesMargin },
    { label: "Settlement Verified", value: (row) => row.settlementVerified ? "yes" : "no" },
    { label: "Country", value: (row) => row.country },
    { label: "Items", value: (row) => row.itemCount },
  ]);

  const exportGateways = () => downloadCsv(exportName("gateways"), gatewayRows, [
    { label: "Gateway", value: (row) => row.label },
    { label: "Orders", value: (row) => row.orders },
    { label: "Paid Orders", value: (row) => row.paidOrders },
    { label: "Unpaid Orders", value: (row) => row.unpaidOrders },
    { label: "Paid Revenue USD", value: (row) => row.paidRevenueUsd },
    { label: "Refund USD", value: (row) => row.refundUsd },
    { label: "Net Revenue USD", value: (row) => row.netRevenueUsd },
    { label: "Gateway Charge %", value: (row) => row.chargePercent },
    { label: "Gateway Fixed USD", value: (row) => row.chargeFixedUsd },
    { label: "Gateway Charge USD", value: (row) => row.gatewayChargeUsd },
    { label: "Profit After Gateway Charge USD", value: (row) => row.profitAfterGatewayChargesUsd },
    { label: "Profit After Gateway Charge Margin", value: (row) => row.profitAfterGatewayChargesMargin },
    { label: "Avg Paid Order USD", value: (row) => row.avgPaidOrderUsd },
    { label: "Sessions", value: (row) => row.sessions },
    { label: "Successful Sessions", value: (row) => row.successfulSessions },
    { label: "Failed Sessions", value: (row) => row.failedSessions },
    { label: "Pending Sessions", value: (row) => row.pendingSessions },
    { label: "Success Rate", value: (row) => row.successRate },
    { label: "Verified Settlements", value: (row) => row.verifiedSettlements },
    { label: "Unverified Settlements", value: (row) => row.unverifiedSettlements },
    { label: "Unresolved Paid Orders", value: (row) => row.unresolvedPaidOrders },
    { label: "Failed Attempts", value: (row) => row.failedAttempts },
  ]);

  const exportDaily = () => downloadCsv(exportName("daily"), dailyRows, [
    { label: "Date", value: (row) => row.date },
    { label: "Orders", value: (row) => row.orders },
    { label: "Paid Orders", value: (row) => row.paidOrders },
    { label: "Unpaid Orders", value: (row) => row.unpaidOrders },
    { label: "Revenue USD", value: (row) => row.revenueUsd },
    { label: "Refund USD", value: (row) => row.refundUsd },
    { label: "Net Revenue USD", value: (row) => row.netRevenueUsd },
    { label: "Gateway Charge USD", value: (row) => row.gatewayChargeUsd },
    { label: "Profit After Gateway Charge USD", value: (row) => row.profitAfterGatewayChargesUsd },
    { label: "Sessions", value: (row) => row.sessions },
    { label: "Successful Sessions", value: (row) => row.successfulSessions },
    { label: "Failed Sessions", value: (row) => row.failedSessions },
    { label: "Unresolved Paid Orders", value: (row) => row.unresolvedPaidOrders },
  ]);

  const exportMonthly = () => downloadCsv(exportName("monthly-report"), monthlyRows, [
    { label: "Month", value: (row) => row.month },
    { label: "Orders", value: (row) => row.orders },
    { label: "Paid Orders", value: (row) => row.paidOrders },
    { label: "Unpaid Orders", value: (row) => row.unpaidOrders },
    { label: "Revenue USD", value: (row) => row.revenueUsd },
    { label: "Refund USD", value: (row) => row.refundUsd },
    { label: "Net Revenue USD", value: (row) => row.netRevenueUsd },
    { label: "Gateway Charge USD", value: (row) => row.gatewayChargeUsd },
    { label: "Profit After Gateway Charge USD", value: (row) => row.profitAfterGatewayChargesUsd },
    { label: "Avg Paid Order USD", value: (row) => row.avgPaidOrderUsd },
    { label: "Sessions", value: (row) => row.sessions },
    { label: "Successful Sessions", value: (row) => row.successfulSessions },
    { label: "Failed Sessions", value: (row) => row.failedSessions },
    { label: "Unresolved Paid Orders", value: (row) => row.unresolvedPaidOrders },
  ]);

  const exportGatewayTransactions = () => downloadCsv(exportName("gateway-transactions"), salesRows, [
    { label: "Gateway", value: (row) => row.providerLabel },
    { label: "Order ID", value: (row) => row.orderId },
    { label: "Customer", value: (row) => row.customer },
    { label: "Finance Date", value: (row) => row.financeDay },
    { label: "Payment Status", value: (row) => row.paymentStatus },
    { label: "Fulfillment Status", value: (row) => row.fulfillmentStatus },
    { label: "USD Revenue", value: (row) => row.usdAmount },
    { label: "Gateway Charge %", value: (row) => row.gatewayChargePercent },
    { label: "Gateway Fixed USD", value: (row) => row.gatewayChargeFixedUsd },
    { label: "Gateway Charge USD", value: (row) => row.gatewayChargeUsd },
    { label: "Profit After Gateway Charge USD", value: (row) => row.profitAfterGatewayChargesUsd },
    { label: "Settlement Verified", value: (row) => row.settlementVerified ? "yes" : "no" },
  ]);

  const exportPayments = () => downloadCsv(exportName("payments"), filteredPaymentRows, [
    { label: "Session ID", value: (row) => row.sessionId },
    { label: "Gateway", value: (row) => row.providerLabel },
    { label: "Status", value: (row) => row.status },
    { label: "Created At", value: (row) => row.createdAt },
    { label: "Amount", value: (row) => row.amount },
    { label: "Currency", value: (row) => row.currency },
    { label: "Billing Country", value: (row) => row.billingCountry },
    { label: "Order ID", value: (row) => row.orderId },
    { label: "Provider Session ID", value: (row) => row.providerSessionId },
    { label: "Provider Payment ID", value: (row) => row.providerPaymentId },
    { label: "Provider Order ID", value: (row) => row.providerOrderId },
    { label: "Settlement Verified", value: (row) => row.settlementVerified ? "yes" : "no" },
    { label: "Failure Reason", value: (row) => row.failureReason },
    { label: "Risk Score", value: (row) => row.riskScore },
    { label: "Risk Flags", value: (row) => row.riskFlags },
  ]);

  const criticalCount =
    Number(totals.unresolvedPaidOrders || 0) +
    Number(totals.failedSessions || 0) +
    Number(totals.unverifiedSettlements || 0);

  const chartRows = dailyRows.map((row) => ({
    ...row,
    label: formatDate(`${row.date}T00:00:00.000Z`),
  }));

  return (
    <AdminPageShell
      title="Finance Management"
      description="Monitor sales, gateway performance, settlement health, refunds, and finance exports."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => setRefreshKey((value) => value + 1)} disabled={loading}>
            <RefreshCw className={cx("h-4 w-4", loading ? "animate-spin" : "")} />
            Refresh
          </Button>
          <Button variant="primary" onClick={exportSales} disabled={!filteredSalesRows.length}>
            <Download className="h-4 w-4" />
            Export Sales
          </Button>
        </div>
      }
    >
      <Card className="p-4 md:p-5 mb-5">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
          <div className="xl:col-span-5 flex flex-wrap gap-2">
            {presetRanges.map((preset) => (
              <Button key={preset.id} onClick={() => setPreset(preset)} className="px-3">
                {preset.label}
              </Button>
            ))}
          </div>
          <label className="xl:col-span-2">
            <span className="mb-1 block text-xs font-bold text-slate-600">From</span>
            <input
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-[#D2042D]/20"
            />
          </label>
          <label className="xl:col-span-2">
            <span className="mb-1 block text-xs font-bold text-slate-600">To</span>
            <input
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-[#D2042D]/20"
            />
          </label>
          <label className="xl:col-span-3">
            <span className="mb-1 block text-xs font-bold text-slate-600">Gateway</span>
            <select
              value={provider}
              onChange={(event) => setProvider(event.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-[#D2042D]/20"
            >
              {providerOptions.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button onClick={exportGateways} disabled={!gatewayRows.length}>
            <Download className="h-4 w-4" />
            Export Gateways
          </Button>
          <Button onClick={exportDaily} disabled={!dailyRows.length}>
            <Download className="h-4 w-4" />
            Export Date Wise
          </Button>
          <Button onClick={exportMonthly} disabled={!monthlyRows.length}>
            <Download className="h-4 w-4" />
            Export Monthly Reports
          </Button>
          <Button onClick={exportGatewayTransactions} disabled={!salesRows.length}>
            <Download className="h-4 w-4" />
            Export Gateway Transactions
          </Button>
          <Button onClick={exportPayments} disabled={!filteredPaymentRows.length}>
            <Download className="h-4 w-4" />
            Export Payments
          </Button>
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-700">
            <CalendarDays className="h-4 w-4" />
            {formatDate(payload?.range?.from)} to {formatDate(payload?.range?.to)}
          </span>
        </div>
      </Card>

      {error ? (
        <div className="mb-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-800">
          {error}
        </div>
      ) : null}

      {criticalCount > 0 ? (
        <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-black">{number(criticalCount)} finance item(s) need attention.</p>
              <p className="mt-1">
                Includes unresolved paid order accounting, failed payment sessions, and unverified settlements.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-5">
        <MetricCard title="Profit After Charges" value={money(totals.profitAfterGatewayChargesUsd)} detail={`${percent(totals.profitAfterGatewayChargesMargin)} of paid revenue after refunds and gateway charge`} icon={TrendingUp} tone="emerald" />
        <MetricCard title="Net Revenue" value={money(totals.netRevenueUsd)} detail="Paid revenue minus recorded refunds" icon={DollarSign} tone="sky" />
        <MetricCard title="Gateway Charges" value={money(totals.gatewayChargeUsd)} detail="Estimated from configured gateway rates" icon={Percent} tone="amber" />
        <MetricCard title="Paid Revenue" value={money(totals.paidRevenueUsd)} detail={`${number(totals.paidOrders)} paid orders`} icon={WalletCards} tone="violet" />
        <MetricCard title="Avg Paid Order" value={money(totals.avgPaidOrderUsd)} detail={`${number(totals.orders)} total orders in range`} icon={FileText} tone="slate" />
        <MetricCard title="Payment Success" value={percent(totals.paymentConversionRate)} detail={`${number(totals.successfulSessions)} successful sessions`} icon={CheckCircle} tone="emerald" />
        <MetricCard title="Refunds" value={money(totals.refundAmountUsd)} detail={`${number(refundRows.filter((row) => row.settled).length)} settled refund records`} icon={RefreshCw} tone="amber" />
        <MetricCard title="Unresolved USD" value={number(totals.unresolvedPaidOrders)} detail="Paid orders missing USD accounting" icon={AlertTriangle} tone="rose" />
        <MetricCard title="Unverified Settlement" value={number(totals.unverifiedSettlements)} detail={`${number(totals.verifiedSettlements)} verified settlements`} icon={ShieldCheck} tone="rose" />
      </div>

      <div className="mb-5 flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-2">
        {[
          { id: "overview", label: "Overview", icon: DollarSign },
          { id: "reports", label: "Reports", icon: CalendarDays },
          { id: "sales", label: "Sales", icon: FileText },
          { id: "gateways", label: "Gateways", icon: CreditCard },
          { id: "payments", label: "Payments", icon: Table2 },
          { id: "charges", label: "Charges", icon: Settings },
          { id: "exceptions", label: "Exceptions", icon: AlertTriangle },
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={cx(
                "inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-bold transition",
                activeTab === tab.id
                  ? "bg-[#D2042D] text-white"
                  : "text-slate-700 hover:bg-slate-50"
              )}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <Card className="p-8 text-sm font-semibold text-slate-600">Loading finance data...</Card>
      ) : null}

      {!loading && activeTab === "overview" ? (
        <OverviewTab chartRows={chartRows} gatewayRows={gatewayRows} totals={totals} />
      ) : null}

      {!loading && activeTab === "reports" ? (
        <ReportsTab rows={monthlyRows} dailyRows={dailyRows} />
      ) : null}

      {!loading && activeTab === "sales" ? (
        <SalesTab rows={filteredSalesRows} search={search} setSearch={setSearch} />
      ) : null}

      {!loading && activeTab === "gateways" ? (
        <GatewayTab rows={gatewayRows} salesRows={salesRows} />
      ) : null}

      {!loading && activeTab === "payments" ? (
        <PaymentsTab rows={filteredPaymentRows} search={paymentSearch} setSearch={setPaymentSearch} />
      ) : null}

      {!loading && activeTab === "charges" ? (
        <ChargeSettingsTab
          rows={chargeDraftRows}
          saving={chargeSaving}
          notice={chargeNotice}
          error={chargeError}
          onChange={updateChargeDraft}
          onSave={saveGatewayCharges}
        />
      ) : null}

      {!loading && activeTab === "exceptions" ? (
        <ExceptionsTab rows={exceptionRows} />
      ) : null}
    </AdminPageShell>
  );
}

function MetricCard({ title, value, detail, icon: Icon, tone = "slate" }) {
  const tones = {
    amber: "border-amber-100 bg-amber-50 text-amber-700",
    emerald: "border-emerald-100 bg-emerald-50 text-emerald-700",
    rose: "border-rose-100 bg-rose-50 text-rose-700",
    sky: "border-sky-100 bg-sky-50 text-sky-700",
    slate: "border-slate-100 bg-slate-50 text-slate-700",
    violet: "border-violet-100 bg-violet-50 text-violet-700",
  };
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-black uppercase text-slate-500">{title}</p>
          <p className="mt-2 text-2xl font-black text-slate-900">{value}</p>
          <p className="mt-1 text-sm text-slate-600">{detail}</p>
        </div>
        <div className={cx("h-11 w-11 shrink-0 rounded-2xl border flex items-center justify-center", tones[tone] || tones.slate)}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

function SectionTitle({ title, subtitle, right }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <div className="flex items-center gap-2">
          <span className="h-5 w-[3px] rounded-full bg-[#D2042D]" />
          <h2 className="text-lg font-black text-slate-900">{title}</h2>
        </div>
        {subtitle ? <p className="mt-1 text-sm text-slate-600">{subtitle}</p> : null}
      </div>
      {right ? <div className="shrink-0">{right}</div> : null}
    </div>
  );
}

function OverviewTab({ chartRows, gatewayRows, totals }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <Card className="p-5">
          <SectionTitle title="Date Wise Revenue" subtitle="Daily paid revenue, refunds, and net revenue in USD." />
          <div className="mt-5 h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartRows} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#64748B" />
                <YAxis tick={{ fontSize: 11 }} stroke="#64748B" />
                <Tooltip formatter={(value, name) => [money(value), name]} />
                <Area type="monotone" dataKey="revenueUsd" name="Revenue" stroke="#16A34A" fill="rgba(22, 163, 74, 0.14)" strokeWidth={2} />
                <Area type="monotone" dataKey="refundUsd" name="Refunds" stroke="#D97706" fill="rgba(217, 119, 6, 0.12)" strokeWidth={2} />
                <Area type="monotone" dataKey="netRevenueUsd" name="Net" stroke="#D2042D" fill="rgba(210, 4, 45, 0.10)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5">
          <SectionTitle title="Gateway Paid Revenue" subtitle="Paid sales grouped by payment gateway." />
          <div className="mt-5 h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={gatewayRows.slice(0, 8)} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#64748B" />
                <YAxis tick={{ fontSize: 11 }} stroke="#64748B" />
                <Tooltip formatter={(value) => [money(value), "Paid revenue"]} />
                <Bar dataKey="paidRevenueUsd" fill="#0F172A" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <SectionTitle title="Finance Health" subtitle="The numbers that decide whether finance needs action today." />
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          <HealthRow icon={CheckCircle} label="Verified settlement orders" value={number(totals.verifiedSettlements)} tone="emerald" />
          <HealthRow icon={Clock} label="Pending payment sessions" value={number(totals.pendingSessions)} tone="amber" />
          <HealthRow icon={AlertTriangle} label="Risk flagged sessions" value={number(totals.riskySessions)} tone="rose" />
        </div>
      </Card>
    </div>
  );
}

function HealthRow({ icon: Icon, label, value, tone }) {
  const tones = {
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
  };
  return (
    <div className={cx("rounded-2xl border p-4", tones[tone])}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-bold">{label}</p>
          <p className="mt-2 text-2xl font-black">{value}</p>
        </div>
        <Icon className="h-6 w-6" />
      </div>
    </div>
  );
}

function ReportsTab({ rows, dailyRows }) {
  return (
    <div className="space-y-5">
      <Card className="p-5">
        <SectionTitle
          title="Monthly Sales & Revenue"
          subtitle="Monthly revenue, refunds, gateway charges, and profit after gateway charges."
        />
        <div className="mt-5 h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#64748B" />
              <YAxis tick={{ fontSize: 11 }} stroke="#64748B" />
              <Tooltip formatter={(value, name) => [money(value), name]} />
              <Bar dataKey="revenueUsd" name="Revenue" fill="#0F172A" radius={[8, 8, 0, 0]} />
              <Bar dataKey="gatewayChargeUsd" name="Gateway charge" fill="#F59E0B" radius={[8, 8, 0, 0]} />
              <Bar dataKey="profitAfterGatewayChargesUsd" name="Profit after charges" fill="#16A34A" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b border-slate-200 p-5">
          <SectionTitle
            title="Report Table"
            subtitle={`${number(dailyRows.length)} date-wise row(s) rolled into ${number(rows.length)} monthly report row(s).`}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {["Month", "Orders", "Paid Orders", "Revenue", "Refunds", "Net Revenue", "Gateway Charges", "Profit After Charges", "AOV", "Sessions"].map((head) => (
                  <th key={head} className="px-4 py-3 text-left text-xs font-black uppercase text-slate-500">{head}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {!rows.length ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-500">No monthly report rows found.</td></tr>
              ) : rows.map((row) => (
                <tr key={row.month} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-black text-slate-900">{row.label || row.month}</td>
                  <td className="px-4 py-3 text-slate-700">{number(row.orders)}</td>
                  <td className="px-4 py-3 text-slate-700">{number(row.paidOrders)}</td>
                  <td className="px-4 py-3 font-black text-slate-900">{money(row.revenueUsd)}</td>
                  <td className="px-4 py-3 text-slate-700">{money(row.refundUsd)}</td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{money(row.netRevenueUsd)}</td>
                  <td className="px-4 py-3 text-slate-700">{money(row.gatewayChargeUsd)}</td>
                  <td className="px-4 py-3 font-black text-emerald-700">{money(row.profitAfterGatewayChargesUsd)}</td>
                  <td className="px-4 py-3 text-slate-700">{money(row.avgPaidOrderUsd)}</td>
                  <td className="px-4 py-3 text-slate-700">
                    <p>{number(row.sessions)} total</p>
                    <p className="text-xs text-slate-500">{number(row.successfulSessions)} success / {number(row.failedSessions)} failed</p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function SalesTab({ rows, search, setSearch }) {
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-slate-200 p-5">
        <SectionTitle
          title="Sales Export View"
          subtitle="Every finance row includes order date, paid date, gateway, transaction currency, USD accounting source, and settlement status."
          right={<SearchBox value={search} onChange={setSearch} placeholder="Search sales..." />}
        />
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {["Order", "Finance Date", "Customer", "Gateway", "Payment", "Original Total", "USD Accounting", "Gateway Charge", "Profit After Charge", "Settlement", "Action"].map((head) => (
                <th key={head} className="px-4 py-3 text-left text-xs font-black uppercase text-slate-500">{head}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!rows.length ? (
              <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-500">No sales rows found.</td></tr>
            ) : rows.map((row) => (
              <tr key={row.id || row.orderId} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <p className="font-black text-slate-900">{row.orderId}</p>
                  <p className="text-xs text-slate-500">{row.itemCount} item(s)</p>
                </td>
                <td className="px-4 py-3 text-slate-700">
                  <p className="font-semibold">{formatDate(row.financeDate)}</p>
                  <p className="text-xs text-slate-500">Paid {formatDate(row.paidAt)}</p>
                </td>
                <td className="px-4 py-3">
                  <p className="font-semibold text-slate-900">{row.customer}</p>
                  <p className="text-xs text-slate-500">{row.email || row.country || "-"}</p>
                </td>
                <td className="px-4 py-3 font-semibold text-slate-900">{row.providerLabel}</td>
                <td className="px-4 py-3">
                  <StatusPill value={row.paymentStatus} />
                  <p className="mt-1 text-xs text-slate-500">{row.fulfillmentStatus}</p>
                </td>
                <td className="px-4 py-3">
                  <p className="font-semibold text-slate-900">{Number(row.amount || 0).toFixed(2)} {row.currency}</p>
                </td>
                <td className="px-4 py-3">
                  <p className="font-black text-slate-900">{row.usdAmount === null ? "Unresolved" : money(row.usdAmount)}</p>
                  <p className="text-xs text-slate-500">{row.usdSource}</p>
                </td>
                <td className="px-4 py-3">
                  <p className="font-black text-slate-900">{money(row.gatewayChargeUsd)}</p>
                  <p className="text-xs text-slate-500">{Number(row.gatewayChargePercent || 0).toFixed(2)}% + {money(row.gatewayChargeFixedUsd)}</p>
                </td>
                <td className="px-4 py-3">
                  <p className="font-black text-emerald-700">{row.usdAmount === null ? "Unresolved" : money(row.profitAfterGatewayChargesUsd)}</p>
                  <p className="text-xs text-slate-500">{percent(row.profitAfterGatewayChargesMargin)}</p>
                </td>
                <td className="px-4 py-3">
                  <StatusPill value={row.settlementVerified ? "Verified" : "Not verified"} />
                </td>
                <td className="px-4 py-3">
                  <Link
                    to={`/administrator/orders/details/${encodeURIComponent(row.id || row.orderId)}`}
                    className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
                  >
                    View
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function GatewayTab({ rows, salesRows }) {
  const [openProvider, setOpenProvider] = useState("");
  const ordersByProvider = useMemo(() => {
    const map = new Map();
    (salesRows || []).forEach((row) => {
      const key = row.provider || "manual";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    });
    return map;
  }, [salesRows]);

  return (
    <Card className="overflow-hidden">
      <div className="border-b border-slate-200 p-5">
        <SectionTitle title="Gateway Wise Transactions" subtitle="Gateway revenue, charges, profit after charges, and attached orders." />
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {["Gateway", "Paid Revenue", "Charges", "Profit After Charges", "Orders", "Sessions", "Success Rate", "Settlements", "Issues", "Attached Orders"].map((head) => (
                <th key={head} className="px-4 py-3 text-left text-xs font-black uppercase text-slate-500">{head}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!rows.length ? (
              <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-500">No gateway data found.</td></tr>
            ) : rows.map((row) => {
              const attachedOrders = ordersByProvider.get(row.provider) || [];
              const isOpen = openProvider === row.provider;
              return (
                <React.Fragment key={row.provider}>
                  <tr className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <p className="font-black text-slate-900">{row.label}</p>
                      <p className="text-xs text-slate-500">{row.provider}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-black text-slate-900">{money(row.paidRevenueUsd)}</p>
                      <p className="text-xs text-slate-500">AOV {money(row.avgPaidOrderUsd)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-black text-slate-900">{money(row.gatewayChargeUsd)}</p>
                      <p className="text-xs text-slate-500">{Number(row.chargePercent || 0).toFixed(2)}% + {money(row.chargeFixedUsd)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-black text-emerald-700">{money(row.profitAfterGatewayChargesUsd)}</p>
                      <p className="text-xs text-slate-500">{percent(row.profitAfterGatewayChargesMargin)}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <p><span className="font-bold text-slate-900">{number(row.paidOrders)}</span> paid</p>
                      <p className="text-xs text-slate-500">{number(row.unpaidOrders)} unpaid / {number(row.orders)} total</p>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <p><span className="font-bold text-slate-900">{number(row.sessions)}</span> sessions</p>
                      <p className="text-xs text-slate-500">{number(row.failedSessions)} failed, {number(row.pendingSessions)} pending</p>
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill value={percent(row.successRate)} />
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <p><span className="font-bold text-slate-900">{number(row.verifiedSettlements)}</span> verified</p>
                      <p className="text-xs text-slate-500">{number(row.unverifiedSettlements)} unverified</p>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <p>{number(row.unresolvedPaidOrders)} unresolved paid orders</p>
                      <p className="text-xs text-slate-500">{number(row.failedAttempts)} failed attempts</p>
                    </td>
                    <td className="px-4 py-3">
                      <Button type="button" onClick={() => setOpenProvider(isOpen ? "" : row.provider)}>
                        {isOpen ? "Hide" : "View"} {number(attachedOrders.length)}
                      </Button>
                    </td>
                  </tr>
                  {isOpen ? (
                    <tr>
                      <td colSpan={10} className="bg-slate-50 px-4 py-4">
                        <div className="rounded-2xl border border-slate-200 bg-white">
                          <div className="border-b border-slate-200 px-4 py-3">
                            <p className="font-black text-slate-900">{row.label} attached orders</p>
                            <p className="text-xs text-slate-500">These are the sales rows counted under this gateway for the selected time frame.</p>
                          </div>
                          <div className="max-h-[420px] overflow-auto">
                            <table className="min-w-full text-sm">
                              <thead className="bg-white">
                                <tr>
                                  {["Order", "Date", "Customer", "Revenue", "Charge", "Profit", "Settlement", "Action"].map((head) => (
                                    <th key={head} className="px-4 py-3 text-left text-xs font-black uppercase text-slate-500">{head}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {!attachedOrders.length ? (
                                  <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">No attached orders found.</td></tr>
                                ) : attachedOrders.map((order) => (
                                  <tr key={order.id || order.orderId} className="hover:bg-slate-50">
                                    <td className="px-4 py-3 font-black text-slate-900">{order.orderId}</td>
                                    <td className="px-4 py-3 text-slate-700">{formatDate(order.financeDate)}</td>
                                    <td className="px-4 py-3">
                                      <p className="font-semibold text-slate-900">{order.customer}</p>
                                      <p className="text-xs text-slate-500">{order.email || "-"}</p>
                                    </td>
                                    <td className="px-4 py-3 font-black text-slate-900">{order.usdAmount === null ? "Unresolved" : money(order.usdAmount)}</td>
                                    <td className="px-4 py-3 text-slate-700">{money(order.gatewayChargeUsd)}</td>
                                    <td className="px-4 py-3 font-black text-emerald-700">{order.usdAmount === null ? "Unresolved" : money(order.profitAfterGatewayChargesUsd)}</td>
                                    <td className="px-4 py-3"><StatusPill value={order.settlementVerified ? "Verified" : "Not verified"} /></td>
                                    <td className="px-4 py-3">
                                      <Link
                                        to={`/administrator/orders/details/${encodeURIComponent(order.id || order.orderId)}`}
                                        className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
                                      >
                                        View
                                      </Link>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ChargeSettingsTab({ rows, saving, notice, error, onChange, onSave }) {
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-slate-200 p-5">
        <SectionTitle
          title="Gateway Charge Settings"
          subtitle="Set the system rate used to calculate gateway charges and profit after charges in finance reports."
          right={
            <Button type="button" variant="primary" onClick={onSave} disabled={saving || !rows.length}>
              <Save className="h-4 w-4" />
              {saving ? "Saving..." : "Save Charges"}
            </Button>
          }
        />
        {notice ? (
          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800">
            {notice}
          </div>
        ) : null}
        {error ? (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-800">
            {error}
          </div>
        ) : null}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {["Gateway", "Use Charge", "Percentage", "Fixed USD", "Formula", "Note"].map((head) => (
                <th key={head} className="px-4 py-3 text-left text-xs font-black uppercase text-slate-500">{head}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!rows.length ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No gateway charge rows found.</td></tr>
            ) : rows.map((row) => (
              <tr key={row.provider} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <p className="font-black text-slate-900">{row.label}</p>
                  <p className="text-xs text-slate-500">{row.provider}</p>
                </td>
                <td className="px-4 py-3">
                  <label className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700">
                    <input
                      type="checkbox"
                      checked={row.enabled !== false}
                      onChange={(event) => onChange(row.provider, { enabled: event.target.checked })}
                      className="h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]"
                    />
                    Enabled
                  </label>
                </td>
                <td className="px-4 py-3">
                  <div className="relative w-[140px]">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={row.percent}
                      onChange={(event) => onChange(row.provider, { percent: event.target.value })}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 pr-8 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-[#D2042D]/20"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-black text-slate-500">%</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="relative w-[140px]">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-black text-slate-500">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={row.fixedUsd}
                      onChange={(event) => onChange(row.provider, { fixedUsd: event.target.value })}
                      className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-7 pr-3 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-[#D2042D]/20"
                    />
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-700">
                  <p className="font-semibold text-slate-900">
                    Revenue x {Number(row.percent || 0).toFixed(2)}% + {money(row.fixedUsd)}
                  </p>
                  <p className="text-xs text-slate-500">Used for order, gateway, monthly, and CSV finance reports.</p>
                </td>
                <td className="px-4 py-3">
                  <input
                    value={row.note}
                    onChange={(event) => onChange(row.provider, { note: event.target.value })}
                    placeholder="Optional note"
                    className="w-[220px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-[#D2042D]/20"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function PaymentsTab({ rows, search, setSearch }) {
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-slate-200 p-5">
        <SectionTitle
          title="Payment Sessions"
          subtitle="Gateway sessions, risk flags, settlement checks, and failure reasons."
          right={<SearchBox value={search} onChange={setSearch} placeholder="Search payments..." />}
        />
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {["Session", "Gateway", "Status", "Amount", "Settlement", "Risk", "Created"].map((head) => (
                <th key={head} className="px-4 py-3 text-left text-xs font-black uppercase text-slate-500">{head}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!rows.length ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No payment sessions found.</td></tr>
            ) : rows.map((row) => (
              <tr key={row.id || row.sessionId} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <p className="font-mono text-xs font-black text-slate-900">{row.sessionId}</p>
                  <p className="text-xs text-slate-500">{row.orderId || "-"}</p>
                </td>
                <td className="px-4 py-3 font-semibold text-slate-900">{row.providerLabel}</td>
                <td className="px-4 py-3">
                  <StatusPill value={row.status} />
                  {row.failureReason ? <p className="mt-1 max-w-[260px] text-xs text-rose-600">{row.failureReason}</p> : null}
                </td>
                <td className="px-4 py-3 font-semibold text-slate-900">{Number(row.amount || 0).toFixed(2)} {row.currency}</td>
                <td className="px-4 py-3">
                  <StatusPill value={row.settlementVerified ? "Verified" : row.settlementRequired ? "Required" : "Not required"} />
                  {row.settlementAmount !== null && row.settlementAmount !== undefined ? (
                    <p className="mt-1 text-xs text-slate-500">{Number(row.settlementAmount).toFixed(2)} {row.settlementCurrency}</p>
                  ) : null}
                </td>
                <td className="px-4 py-3">
                  <p className="font-semibold text-slate-900">{row.riskScore || 0}</p>
                  <p className="text-xs text-slate-500">{Array.isArray(row.riskFlags) && row.riskFlags.length ? row.riskFlags.join(", ") : "No flags"}</p>
                </td>
                <td className="px-4 py-3 text-slate-700">{formatDateTime(row.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ExceptionsTab({ rows }) {
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-slate-200 p-5">
        <SectionTitle title="Finance Exceptions" subtitle="Accounting, settlement, payment, and refund items requiring review." />
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {["Severity", "Group", "Reference", "Gateway", "Message"].map((head) => (
                <th key={head} className="px-4 py-3 text-left text-xs font-black uppercase text-slate-500">{head}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!rows.length ? (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center">
                  <CheckCircle className="mx-auto h-10 w-10 text-emerald-500" />
                  <p className="mt-3 font-bold text-slate-900">No finance exceptions in this range.</p>
                </td>
              </tr>
            ) : rows.map((row, index) => (
              <tr key={`${row.group}-${row.type}-${row.orderId || row.sessionId || row.refundId || index}`} className="hover:bg-slate-50">
                <td className="px-4 py-3"><StatusPill value={row.severity} /></td>
                <td className="px-4 py-3 font-semibold capitalize text-slate-900">{row.group}</td>
                <td className="px-4 py-3 font-mono text-xs text-slate-700">{row.orderId || row.sessionId || row.refundId || "-"}</td>
                <td className="px-4 py-3 text-slate-700">{row.provider || "-"}</td>
                <td className="px-4 py-3 text-slate-700">{row.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function SearchBox({ value, onChange, placeholder }) {
  return (
    <div className="relative w-full sm:w-[280px]">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-[#D2042D]/20"
      />
    </div>
  );
}
